import React from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';

const credentialsSchema = z.object({
  pit: z.string().min(1, 'Private Integration Token is required'),
  locationId: z.string().min(1, 'Location ID is required'),
});

type CredentialsFormData = z.infer<typeof credentialsSchema>;

type ZoneDMCredentialsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CredentialsFormData) => void;
  isLoading?: boolean;
  initialData?: {
    pit: string;
    locationId: string;
  };
  oauthStatus?: 'connected' | 'not_connected' | 'error';
  connectionMethod?: 'oauth' | 'manual';
  onStartOAuth?: () => void;
};

const TABS = [
  { key: 'oauth', label: 'Connect with Login' },
  { key: 'manual', label: 'Manual Connection' },
];

export default function ZoneDMCredentialsModal({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  initialData,
  oauthStatus = 'not_connected',
  connectionMethod = 'manual',
  onStartOAuth,
}: ZoneDMCredentialsModalProps) {
  const [activeTab, setActiveTab] = React.useState<'oauth' | 'manual'>(connectionMethod);

  React.useEffect(() => {
    setActiveTab(connectionMethod);
  }, [connectionMethod]);

  React.useEffect(() => {
    if (!isOpen) return;
    // Remember last used tab in localStorage
    const lastTab = window.localStorage.getItem('zonedm-credentials-tab');
    if (lastTab === 'oauth' || lastTab === 'manual') {
      setActiveTab(lastTab);
    }
  }, [isOpen]);

  const handleTabChange = (tab: 'oauth' | 'manual') => {
    setActiveTab(tab);
    window.localStorage.setItem('zonedm-credentials-tab', tab);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: initialData,
  });

  React.useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const onSubmit = async (data: CredentialsFormData) => {
    try {
      await onSave(data);
    } catch (error) {
      toast.error('Failed to save credentials');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg">
          <div className="relative p-6">
            <button
              onClick={onClose}
              className="absolute right-6 top-6 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-8">
              ZoneDM Connection
            </h3>

            {/* Tabs */}
            <div className="flex border-b mb-6">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm focus:outline-none ${
                    activeTab === tab.key
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-indigo-600'
                  }`}
                  onClick={() => handleTabChange(tab.key as 'oauth' | 'manual')}
                  type="button"
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'oauth' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">CRM OAuth Connection</span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      oauthStatus === 'connected'
                        ? 'bg-green-100 text-green-800'
                        : oauthStatus === 'error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {oauthStatus === 'connected'
                      ? 'Connected via OAuth'
                      : oauthStatus === 'error'
                      ? 'Error'
                      : 'Not Connected'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onStartOAuth}
                  className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {oauthStatus === 'connected' ? 'Reconnect Your CRM' : 'Connect Your CRM'}
                </button>
                <div className="text-xs text-gray-500">
                  You will be redirected to your CRM to authorize access. If already connected, you may disconnect by switching to manual mode.
                </div>
              </div>
            )}

            {activeTab === 'manual' && (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Private Integration Token (PIT)
                  </label>
                  <input
                    type="password"
                    {...register('pit')}
                    className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter your PIT"
                  />
                  {errors.pit && (
                    <p className="mt-1 text-sm text-red-600">{errors.pit.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Location ID
                  </label>
                  <input
                    type="text"
                    {...register('locationId')}
                    className="mt-2 block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter your Location ID"
                  />
                  {errors.locationId && (
                    <p className="mt-1 text-sm text-red-600">{errors.locationId.message}</p>
                  )}
                </div>

                <div className="mt-8 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isLoading ? 'Testing Connection...' : 'Test Connection'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}